import { AuthenticatedApiRequest } from '@goldspotdiscoveries/auth';
import { S3Response } from '../../TypeScript/types';
import { parseQueryParams } from '../helpers/parse-query-params';

interface DatasetFile {
  name: string;
  type: Blob['type'];
  lastModified: number;
  size: number;
  project?: string;
}

/**
 * Get a json file and return the contents
 * @param path permanent path
 * @returns Object stored in .json file
 */
export const getJsonFile = <T = any>(path: string): Promise<T> => {
  return new Promise((resolve, reject) => {
    AuthenticatedApiRequest({
      method: 'get',
      apiName: 'Files',
      path: `/${path}`,
    })
      .then((result: any) => {
        const url: string = result.result;
        return fetch(url);
      })
      .then((response: Response) => {
        return response.json();
      })
      .then((jsonFile: any) => {
        resolve(jsonFile);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// Lists all files/folders within a given folder
export const getFilesS3 = (folderPath: string, queryParams = {}) => {
  const validKeys: string[] = ['limit', 'sortBy', 'offset_token', 'type'];
  const queryString: string = parseQueryParams(validKeys, queryParams);
  return AuthenticatedApiRequest<S3Response>({
    method: 'get',
    apiName: 'Files',
    path: `/${folderPath}${queryString}`,
  });
};

/**
 * Retrieves a single file from S3 storage.
 * @param filePath - The path to the file in S3 storage
 * @returns A promise that resolves to a fetch Response object containing the file data
 * @throws {Error} When the API response result is not a string (invalid file response)
 */
export const getSingleFileS3 = (filePath: string) =>
  AuthenticatedApiRequest<{ result: string }>({
    method: 'get',
    apiName: 'Files',
    path: `/${filePath}`,
  }).then((response) => {
    if (typeof response.result === 'string') {
      return fetch(response.result);
    } else {
      throw new Error('Invalid file response');
    }
  });

export const downloadFileFromS3 = (path: string, fileName: string) => {
  return new Promise((resolve, reject) => {
    /**
     * Generic browser download feature
     * @param data A CSV string
     */
    const download = (
      data: string | ArrayBuffer,
      fileData: DatasetFile,
    ): void => {
      // make csv string into file
      const file: Blob = new Blob([data], { type: fileData.type });
      // create an anchor element
      const anchorElement: HTMLAnchorElement = document.createElement('a');
      // make the anchor link to the file
      anchorElement.href = window.URL.createObjectURL(file);
      // make the anchor link invisible and out of the document flow
      anchorElement.style.display = 'none';
      // give the file a name
      anchorElement.download = `${fileData.name}`;
      // add the anchor element to the DOM
      document.body.appendChild(anchorElement);
      // click the link (file will download)
      anchorElement.click();
      // remove the anchor element from the DOM
      document.body.removeChild(anchorElement);
    };
    const file: any = {};
    AuthenticatedApiRequest({
      method: 'get',
      apiName: 'Files',
      path: `/${path}`,
    })
      .then((response: any) => {
        if (response.result && typeof response.result === 'string') {
          return fetch(response.result);
        } else {
          throw new Error('No file found');
        }
      })
      .then((response: Response) => {
        file.type = response.headers.get('Content-Type') || '';
        return response.blob();
      })
      .then((data: any) => {
        file.name = fileName;
        file.size = data.size;
        download(data as string | ArrayBuffer, file);
        resolve(true);
      })
      .catch((reason) => {
        reject(reason);
      });
  });
};
